import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Close, AccessTime } from '@mui/icons-material';

interface RadialTimePickerProps {
  value?: string; // Format: "3pm", "11am", etc.
  onChange: (time: string) => void; // Callback with time string like "3pm"
  label: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

const RadialTimePicker: React.FC<RadialTimePickerProps> = ({
  value,
  onChange,
  label,
  required = false,
  error = false,
  helperText,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<number>(9); // 0-23
  const [selectedPeriod, setSelectedPeriod] = useState<'am' | 'pm'>('am');

  useEffect(() => {
    if (value) {
      parseTime(value);
    }
  }, [value]);

  const parseTime = (timeStr: string) => {
    const normalized = timeStr.trim().toLowerCase();
    const match = normalized.match(/^(\d{1,2})(am|pm)$/);
    if (match) {
      let hour = parseInt(match[1], 10);
      const period = match[2] as 'am' | 'pm';
      
      if (period === 'am') {
        if (hour === 12) {
          setSelectedHour(0);
        } else {
          setSelectedHour(hour);
        }
        setSelectedPeriod('am');
      } else {
        if (hour === 12) {
          setSelectedHour(12);
        } else {
          setSelectedHour(hour + 12);
        }
        setSelectedPeriod('pm');
      }
    }
  };

  const getDisplayTime = (): string => {
    if (!value) return 'Select time';
    return value;
  };

  const getSelectedHour12 = (): number => {
    if (selectedPeriod === 'am') {
      return selectedHour === 0 ? 12 : selectedHour;
    } else {
      return selectedHour === 12 ? 12 : selectedHour - 12;
    }
  };

  const handleHourSelect = (hour: number) => {
    const newHour = selectedPeriod === 'am' 
      ? (hour === 12 ? 0 : hour)
      : (hour === 12 ? 12 : hour + 12);
    setSelectedHour(newHour);
    updateTime(newHour, selectedPeriod);
  };

  const handlePeriodSelect = (period: 'am' | 'pm') => {
    let newHour = selectedHour;
    if (period === 'am' && selectedHour >= 12) {
      newHour = selectedHour === 12 ? 0 : selectedHour - 12;
    } else if (period === 'pm' && selectedHour < 12) {
      newHour = selectedHour === 0 ? 12 : selectedHour + 12;
    }
    setSelectedHour(newHour);
    setSelectedPeriod(period);
    updateTime(newHour, period);
  };

  const updateTime = (hour: number, period: 'am' | 'pm') => {
    let displayHour: number;
    if (period === 'am') {
      displayHour = hour === 0 ? 12 : hour;
    } else {
      displayHour = hour === 12 ? 12 : hour - 12;
    }
    const timeString = `${displayHour}${period}`;
    onChange(timeString);
  };

  const handleConfirm = () => {
    updateTime(selectedHour, selectedPeriod);
    setOpen(false);
  };

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const selectedHour12 = getSelectedHour12();

  return (
    <>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: error ? 'error.main' : '#666',
            mb: 0.5,
            display: 'block',
          }}
        >
          {label} {required && '*'}
        </Typography>
        <Box
          onClick={() => setOpen(true)}
          sx={{
            border: `1px solid ${error ? 'error.main' : '#e0e0e0'}`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            backgroundColor: '#fff',
            '&:hover': {
              borderColor: error ? 'error.main' : '#081122',
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: value ? '#081122' : '#999',
              fontWeight: value ? 500 : 400,
            }}
          >
            {getDisplayTime()}
          </Typography>
          <AccessTime sx={{ color: '#081122', fontSize: 24 }} />
        </Box>
        {helperText && (
          <Typography
            variant="caption"
            sx={{ color: error ? 'error.main' : '#666', mt: 0.5, ml: 1.5 }}
          >
            {helperText}
          </Typography>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Select Time
            </Typography>
            <IconButton onClick={() => setOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
              {/* Radial Hour Picker */}
              <Box
                sx={{
                  position: 'relative',
                  width: 300,
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Circle */}
                <Box
                  sx={{
                    position: 'absolute',
                    width: 250,
                    height: 250,
                    borderRadius: '50%',
                    border: '2px solid #e0e0e0',
                  }}
                />
                
                {/* Hour Numbers */}
                {hours.map((hour, index) => {
                  const angle = (index * 30 - 90) * (Math.PI / 180);
                  const radius = 100;
                  const x = 150 + radius * 0.7 * Math.cos(angle);
                  const y = 150 + radius * 0.7 * Math.sin(angle);
                  const isSelected = selectedHour12 === hour;
                  
                  return (
                    <Box
                      key={hour}
                      onClick={() => handleHourSelect(hour)}
                      sx={{
                        position: 'absolute',
                        left: x - 20,
                        top: y - 20,
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: isSelected ? '#081122' : 'transparent',
                        border: `2px solid ${isSelected ? '#081122' : '#e0e0e0'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: isSelected ? '#081122' : '#f5f5f5',
                          borderColor: '#081122',
                        },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: isSelected ? 'bold' : 'normal',
                          color: isSelected ? '#fff' : '#081122',
                        }}
                      >
                        {hour}
                      </Typography>
                    </Box>
                  );
                })}
                
                {/* Center Dot */}
                <Box
                  sx={{
                    position: 'absolute',
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#081122',
                  }}
                />
              </Box>

              {/* AM/PM Selector */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant={selectedPeriod === 'am' ? 'contained' : 'outlined'}
                  onClick={() => handlePeriodSelect('am')}
                  sx={{
                    minWidth: 80,
                    py: 2,
                    backgroundColor: selectedPeriod === 'am' ? '#081122' : 'transparent',
                    color: selectedPeriod === 'am' ? '#fff' : '#081122',
                    borderColor: '#081122',
                    '&:hover': {
                      backgroundColor: selectedPeriod === 'am' ? '#1a2a3a' : '#f5f5f5',
                    },
                  }}
                >
                  AM
                </Button>
                <Button
                  variant={selectedPeriod === 'pm' ? 'contained' : 'outlined'}
                  onClick={() => handlePeriodSelect('pm')}
                  sx={{
                    minWidth: 80,
                    py: 2,
                    backgroundColor: selectedPeriod === 'pm' ? '#081122' : 'transparent',
                    color: selectedPeriod === 'pm' ? '#fff' : '#081122',
                    borderColor: '#081122',
                    '&:hover': {
                      backgroundColor: selectedPeriod === 'pm' ? '#1a2a3a' : '#f5f5f5',
                    },
                  }}
                >
                  PM
                </Button>
              </Box>
            </Box>

            {/* Selected Time Display */}
            <Box
              sx={{
                mt: 4,
                textAlign: 'center',
                p: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
              }}
            >
              <Typography variant="h5" sx={{ color: '#081122', fontWeight: 'bold' }}>
                {selectedHour12}{selectedPeriod}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" sx={{ backgroundColor: '#081122' }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RadialTimePicker;

